import { logError } from '@ptek/client-logger';
// Transition constants
const TRANSITION_DURATION = '0.3s';
const TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
// Default sizes for compact and expanded modes (aligned with app defaults)
const DEFAULT_COMPACT_SIZE = { width: 400, height: 180 };
const DEFAULT_EXPANDED_SIZE = { width: 400, height: 800 };
/**
 * Manages expand/collapse functionality for widgets
 * Works with predefined compact and expanded sizes rather than just hiding content
 */
export class ExpandCollapseManager {
    constructor(onStateChange) {
        Object.defineProperty(this, "isExpandedState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "container", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "contentContainer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "onStateChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "originalTransition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "compactSize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: DEFAULT_COMPACT_SIZE
        });
        Object.defineProperty(this, "expandedSize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: DEFAULT_EXPANDED_SIZE
        });
        Object.defineProperty(this, "preCollapsePosition", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "boundaryConstraintFn", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.onStateChange = onStateChange;
    }
    /**
     * Sets the container elements that will be managed for expand/collapse
     */
    setContainers(container, contentContainer) {
        this.container = container;
        this.contentContainer = contentContainer;
        if (container) {
            this.originalTransition = container.style.transition || '';
        }
    }
    /**
     * Sets custom sizes for compact and expanded modes
     */
    setSizes(compactSize, expandedSize) {
        this.compactSize = compactSize;
        this.expandedSize = expandedSize;
    }
    /**
     * Sets a callback function to be called whenever the expand/collapse state changes
     */
    setStateChangeCallback(callback) {
        const originalCallback = this.onStateChange;
        this.onStateChange = (isExpanded) => {
            if (originalCallback) {
                originalCallback(isExpanded);
            }
            callback(isExpanded);
        };
    }
    /**
     * Collapses the widget to compact mode
     */
    async collapse() {
        if (!this.container || !this.contentContainer || !this.isExpandedState) {
            return false;
        }
        try {
            this.preCollapsePosition = {
                x: parseInt(this.container.style.left) || 0,
                y: parseInt(this.container.style.top) || 0,
            };
            this.applyCollapseStyles();
            this.isExpandedState = false;
            this.onStateChange?.(false);
            return true;
        }
        catch (error) {
            logError('ExpandCollapseManager', 'Failed to collapse widget:', error);
            return false;
        }
    }
    /**
     * Expands the widget to full mode
     */
    async expand() {
        if (!this.container || !this.contentContainer || this.isExpandedState) {
            return false;
        }
        try {
            this.applyExpandStyles();
            this.isExpandedState = true;
            this.onStateChange?.(true);
            return true;
        }
        catch (error) {
            logError('ExpandCollapseManager', 'Failed to expand widget:', error);
            return false;
        }
    }
    /**
     * Toggles expand/collapse state
     */
    async toggleExpandCollapse() {
        if (this.isExpandedState) {
            return this.collapse();
        }
        else {
            return this.expand();
        }
    }
    /**
     * Returns current expanded state
     */
    isExpanded() {
        return this.isExpandedState;
    }
    /**
     * Gets the compact size
     */
    getCompactSize() {
        return this.compactSize;
    }
    /**
     * Gets the expanded size
     */
    getExpandedSize() {
        return this.expandedSize;
    }
    /**
     * Sets the initial state (used when widget is first created)
     */
    setInitialState(isExpanded) {
        this.isExpandedState = isExpanded;
    }
    /**
     * Sets the pre-collapse position for proper expand/collapse animations
     */
    setPreCollapsePosition(position) {
        this.preCollapsePosition = position;
    }
    /**
     * Sets a boundary constraint function to ensure expand/collapse positions stay within bounds
     */
    setBoundaryConstraintFunction(constraintFn) {
        this.boundaryConstraintFn = constraintFn;
    }
    /**
     * Applies collapse styles to show compact mode
     * Keeps the header (top) fixed while the bottom section moves up
     */
    applyCollapseStyles() {
        if (!this.container || !this.contentContainer || !this.preCollapsePosition)
            return;
        this.container.style.transition = `all ${TRANSITION_DURATION} ${TRANSITION_EASING}`;
        // Keep the top position fixed - header remains in place, bottom moves up
        let newPosition = {
            x: this.preCollapsePosition.x,
            y: this.preCollapsePosition.y,
        };
        if (this.boundaryConstraintFn) {
            newPosition = this.boundaryConstraintFn(newPosition, this.compactSize);
        }
        this.container.style.width = `${this.compactSize.width}px`;
        this.container.style.height = `${this.compactSize.height}px`;
        this.container.style.left = `${newPosition.x}px`;
        this.container.style.top = `${newPosition.y}px`;
        this.container.classList.add('widget-collapsed');
        this.container.classList.remove('widget-expanded');
        setTimeout(() => {
            if (this.container) {
                this.container.style.transition = this.originalTransition;
            }
        }, 300);
    }
    /**
     * Applies expand styles to show full mode
     */
    applyExpandStyles() {
        if (!this.container || !this.contentContainer || !this.preCollapsePosition)
            return;
        this.container.style.transition = `all ${TRANSITION_DURATION} ${TRANSITION_EASING}`;
        const newPosition = {
            x: this.preCollapsePosition.x,
            y: this.preCollapsePosition.y,
        };
        if (this.boundaryConstraintFn) {
            const constrained = this.boundaryConstraintFn(newPosition, this.expandedSize);
            this.container.style.left = `${constrained.x}px`;
            this.container.style.top = `${constrained.y}px`;
        }
        else {
            this.container.style.left = `${newPosition.x}px`;
            this.container.style.top = `${newPosition.y}px`;
        }
        this.container.style.width = `${this.expandedSize.width}px`;
        this.container.style.height = `${this.expandedSize.height}px`;
        this.container.classList.add('widget-expanded');
        this.container.classList.remove('widget-collapsed');
        setTimeout(() => {
            if (this.container) {
                this.container.style.transition = this.originalTransition;
            }
        }, 300);
    }
    reset() {
        if (this.isExpandedState) {
            this.isExpandedState = false;
        }
    }
    /**
     * Cleanup method
     */
    destroy() {
        this.reset();
        this.container = null;
        this.contentContainer = null;
        this.preCollapsePosition = null;
        this.boundaryConstraintFn = undefined;
    }
}
//# sourceMappingURL=ExpandCollapseManager.js.map